import { Driver, Vehicle } from '../Vehicle';
import { LaneItem, LevelManager } from './LevelManager';
import { TrafficSkinsPool, VehicleSkins } from './../CharacterSpine';
import { MatterPhysicsUpdater } from '../core/modules/MatterPhysicsUpdater';
import { VehicleFactory } from './VehicleFactory';
import { Level, TrafficDetails } from './levels/level';
import { Theme } from './themes/theme';

export interface TrafficVehicle {
  vehicle: Vehicle;
  lastSpeedChange: number;
}

export class TrafficManager {
  protected scene: Phaser.Scene;
  protected vehicleFactory: VehicleFactory;
  protected levelManager: LevelManager;
  protected matterUpdater: MatterPhysicsUpdater;
  protected vehicleLayer: Phaser.GameObjects.Layer;
  protected gameUILayer: Phaser.GameObjects.Layer;

  protected trafficDetails: TrafficDetails;
  protected initialPoolSize: number;
  protected behindDistanceX: number;

  protected isActive: boolean;
  protected vehiclePool: Set<TrafficVehicle>;
  protected liveVehicles: Set<TrafficVehicle>;
  protected targetTrafficLevel: number;
  protected lastLaneSpawn: number[];
  protected nextSpawnTime: number;

  protected lastSkinIndex: number;

  constructor(
    scene: Phaser.Scene,
    vehicleFactory: VehicleFactory,
    levelManager: LevelManager,
    matterUpdater: MatterPhysicsUpdater,
    vehicleLayer: Phaser.GameObjects.Layer,
    gameUILayer: Phaser.GameObjects.Layer
  ) {
    this.scene = scene;
    this.vehicleFactory = vehicleFactory;
    this.levelManager = levelManager;
    this.matterUpdater = matterUpdater;
    this.vehicleLayer = vehicleLayer;
    this.gameUILayer = gameUILayer;

    this.trafficDetails = this.levelManager.CurrentLevel.trafficDetails;
    this.initialPoolSize = 3;
    this.behindDistanceX = 1000;

    this.isActive = false;
    this.vehiclePool = new Set();
    this.liveVehicles = new Set();
    this.targetTrafficLevel = 6;
    this.lastLaneSpawn = [];
    this.nextSpawnTime = 0;

    this.lastSkinIndex = 0;
  }

  public init(level: Level, theme: Theme) {
    this.trafficDetails = level.trafficDetails;
    this.initialPoolSize = this.trafficDetails.maxVehicles;
    this.lastLaneSpawn.fill(-1, 0, theme.roadDetails.laneCount);

    this.buildPool(this.initialPoolSize);
  }

  public start() {
    this.isActive = true;
  }

  public pause() {
    this.isActive = false;
  }

  public resume() {
    this.isActive = true;
  }

  public end() {
    this.isActive = false;
  }

  // public findNearestTrafficToPoint(lineX: number, nextTraffic: Map<number, LaneItem | undefined>): Map<number, LaneItem | undefined> {
  //   for (const trafficVehicle of this.liveVehicles) {
  //     let laneItem = nextTraffic.get(trafficVehicle.vehicle.CurrentLane);

  //     if (trafficVehicle.vehicle.x > lineX && (!laneItem || trafficVehicle.vehicle.x < laneItem.distance)) {
  //       if (!laneItem) laneItem = { className: Vehicle.name, distance: 0 };
  //       laneItem.distance = trafficVehicle.vehicle.x;
  //       nextTraffic.set(trafficVehicle.vehicle.CurrentLane, laneItem);
  //     }
  //   }

  //   return nextTraffic;
  // }

  public buildPool(poolSize: number) {
    for (let i = 0; i < poolSize; i++) {
      let vehicle = this.vehicleFactory.makeVehicle(this.vehicleFactory.makeTrafficProfile(TrafficSkinsPool[i % TrafficSkinsPool.length]), 0, -1000);
      this.recoverVehicle({ vehicle: vehicle, lastSpeedChange: 0 });
    }

    this.lastSkinIndex = poolSize - 1;
  }

  public updateTraffic(time: number, delta: number) {
    if (!this.isActive) return;

    let camera = this.scene.cameras.main;
    let behindLine = camera.worldView.left - this.behindDistanceX;
    let aheadLine = camera.worldView.right;

    // Sort vehicles into those ahead and behind offscreen.
    let [vehiclesBehind, vehiclesAhead, freeLanes] = this.sortTraffic(behindLine, aheadLine);

    // Check if any vehicles are too far back and recover them.
    // this.checkForVehiclesToRecover(0);
    this.recoverVehicles(vehiclesBehind);

    // Check current traffic level.
    // If below desired traffic level spawn more traffic.
    this.spawnNeededTraffic(time, aheadLine, freeLanes);

    // Update speeds.
    for (const trafficVehicle of this.liveVehicles) {
      if (
        trafficVehicle.lastSpeedChange + Phaser.Math.Between(this.trafficDetails.minTimeBetweenSpeedChanges, this.trafficDetails.maxTimeBetweenSpeedChanges) <=
        time
      ) {
        let profile = this.vehicleFactory.makeTrafficProfile(trafficVehicle.vehicle.DriverProfile.skin);
        trafficVehicle.vehicle.setupDriverProfile(profile);
        trafficVehicle.lastSpeedChange = time;
      }
    }
  }

  protected canSpawnTraffic(time: number): boolean {
    return this.nextSpawnTime - time <= 0;
  }

  protected sortTraffic(behindLine: number, aheadLine: number): [vehiclesBehind: TrafficVehicle[], vehiclesAhead: TrafficVehicle[], freeLanes: number[]] {
    let vehiclesBehind: TrafficVehicle[] = [];
    let vehiclesAhead: TrafficVehicle[] = [];
    let freeLanes: Set<number> = new Set([...Array(this.levelManager.CurrentTheme.roadDetails.laneCount).keys()]);
    // let blockedLanes: Set<number> = new Set();

    for (const trafficVehicle of this.liveVehicles) {
      if (trafficVehicle.vehicle.x < behindLine) {
        vehiclesBehind.push(trafficVehicle);
      } else if (trafficVehicle.vehicle.x > aheadLine) {
        vehiclesAhead.push(trafficVehicle);
        if (freeLanes.has(trafficVehicle.vehicle.CurrentLane)) freeLanes.delete(trafficVehicle.vehicle.CurrentLane);
        // if (!blockedLanes.has(vehicle.CurrentLane)) blockedLanes.add(vehicle.CurrentLane);
      } else {
        // Change vehicle speed.
      }
    }

    return [vehiclesBehind, vehiclesAhead, [...freeLanes.keys()]];
  }

  protected spawnNeededTraffic(time: number, aheadLine: number, freeLanes: number[]) {
    let missingVehicles = Math.min(this.targetTrafficLevel - this.liveVehicles.size, this.trafficDetails.maxVehicles);

    if (missingVehicles > 0 && freeLanes.length > this.trafficDetails.maxLanesAtOnce && this.canSpawnTraffic(time)) {
      // if (missingVehicles > 0) {
      for (let i = 0; i < missingVehicles; i++) {
        if (freeLanes.length <= 0) break;

        if (this.vehiclePool.size > 0) {
          let vehicle: TrafficVehicle = this.vehiclePool.values().next().value;
          this.vehiclePool.delete(vehicle);

          vehicle.vehicle.switchSkin(TrafficSkinsPool[this.lastSkinIndex % TrafficSkinsPool.length]);
          this.lastSkinIndex++;

          // let [lane, distance] = this.findNextFreeLane();
          let randomLaneIndex = Phaser.Math.Between(0, freeLanes.length - 1);
          let lane = freeLanes.splice(randomLaneIndex, 1)[0];
          let distance = aheadLine + Phaser.Math.Between(this.trafficDetails.minSpawnAheadDistance, this.trafficDetails.maxSpawnAheadDistance);
          // freeLanes.delete(lane);
          this.nextSpawnTime = time + Phaser.Math.Between(this.trafficDetails.minTimeBetweenSpawns, this.trafficDetails.maxTimeBetweenSpawns);
          this.placeAndActivateVehicle(vehicle, lane, distance);
          break;
        }
      }
    }
  }

  protected placeAndActivateVehicle(trafficVehicle: TrafficVehicle, laneNumber: number, distanceX: number) {
    trafficVehicle.vehicle.setActive(true);
    trafficVehicle.vehicle.setVisible(true);
    trafficVehicle.vehicle.teleportToPositionInLane(laneNumber, distanceX);
    // vehicle.setPosition(distanceX, this.levelManager.getLaneY(laneNumber));
    trafficVehicle.lastSpeedChange = this.scene.game.loop.time;
    this.liveVehicles.add(trafficVehicle);

    this.lastLaneSpawn[laneNumber] = distanceX;
    trafficVehicle.vehicle.startMoving();
  }

  protected findNextFreeLane(): [lane: number, distanceX: number] {
    let freeLane = 0;
    let laneDistance = Number.MAX_SAFE_INTEGER;

    this.lastLaneSpawn.forEach((distance, lane) => {
      if (distance < laneDistance) {
        freeLane = lane;
        laneDistance = distance;
      }
    });

    return [freeLane, laneDistance];
  }

  // protected createVehicleProfile() {
  //   return this.vehicleFactory.makeTrafficProfile(VehicleSkins.CarGreen);
  // }

  // protected createVehicle(lane: number, distanceX: number): Vehicle {
  //   let vehicle = new Vehicle(this.scene, this.levelManager.CurrentLevel.roadDetails, {
  //     profile: this.createVehicleProfile(),
  //     vehicleLayer: this.vehicleLayer,
  //     gameUILayer: this.gameUILayer,
  //     startLane: lane,
  //     startIndentX: distanceX
  //   });

  //   this.matterUpdater.add(vehicle.physicsUpdate, vehicle, 0);
  //   this.vehicleLayer.add(vehicle);

  //   return vehicle;
  // }

  protected recoverVehicles(vehiclesToRecover: TrafficVehicle[]) {
    for (const trafficVehicle of vehiclesToRecover) {
      this.recoverVehicle(trafficVehicle);
    }
  }

  protected recoverVehicle(trafficVehicle: TrafficVehicle) {
    trafficVehicle.vehicle.stopMoving();
    this.scene.tweens.killTweensOf(trafficVehicle.vehicle);
    trafficVehicle.vehicle.setPosition(-1000, 0);
    trafficVehicle.vehicle.setActive(false);
    // trafficVehicle.vehicle.setVisible(false); <--- Warning: causes weird issues.

    if (this.liveVehicles.has(trafficVehicle)) this.liveVehicles.delete(trafficVehicle);
    if (!this.vehiclePool.has(trafficVehicle)) this.vehiclePool.add(trafficVehicle);
  }
}
